import React, { useState } from "react";
import { IconButton, InputAdornment, Typography } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import MailIcon from "@mui/icons-material/Mail";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import RudderLogo from "../../img/Starlight Logo.png";

import TextFieldInput from "../../components/Form/TextFieldInput";
import ButtonComponent from "../../components/Form/ButtonComponent";
import Toast from "../../components/general/Toast";

import { setRoleWiseMenu } from "../../store/features/roleWiseMenuSlice";
import { setUser } from "../../store/features/authSlice";
import { AppRoutes } from "../../routes/AppRoutes";
import { regex } from "../../_constants/regex";
import { validationMessage } from "../../_constants/errorMessage";
import { ApiRoutes } from "../../routes/apiRoutes";
import useFetcher from "../../hooks/useFetcher";
import { setDateTimeData, SetSystemConfiguration } from "../../actions";
import { queryClient } from "../../_apiConfig/queryClient";

const defaultFormData = {
  email: "",
  password: "",
};

function LoginPage() {
  const notify = Toast();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showPassword, setShowPassword] = useState(false);

  const { control, handleSubmit, reset } = useForm({
    defaultValues: defaultFormData,
    mode: "onChange",
  });

  const { fetch: submitForm, loading: loginLoading } = useFetcher();
  const onSubmit = async (formData) => {
    try {
      const loginRes = await submitForm({
        method: "POST",
        url: `${ApiRoutes.AUTH.LOGIN}`,
        data: formData,
      });

      if (loginRes?.success) {
        queryClient.clear();
        localStorage.setItem("token", loginRes.data.token);

        notify(loginRes.message, "success");

        const firstMenuUrl =
          loginRes?.data?.access?.menus?.[0]?.url || AppRoutes.DASHBOARD.INDEX;

        navigate(firstMenuUrl);

        dispatch(setRoleWiseMenu(loginRes?.data?.access));

        dispatch(
          SetSystemConfiguration(loginRes?.data?.access?.systemConfiguration),
        );

        dispatch(setDateTimeData(loginRes?.data?.access?.dateTimeSettings));

        dispatch(setUser(loginRes?.data?.user));

        reset(defaultFormData);

        return true;
      } else {
        notify(loginRes.message, "error");
        return false;
      }
    } catch (error) {
      console.error("Login failed:", error);
      notify("Something went wrong", "error");
      return false;
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <main>
      <div className="row m-0 p-0" style={{ height: "100vh" }} id="test">
        <div className="col-md-12 col-sm-12">
          <div className="container h-100">
            <div className="row h-100">
              <div className="col-md-4 col-sm-12 mx-auto h-100 align-items-start justify-content-center d-flex flex-column">
                <img
                  className="mb-0"
                  height={70}
                  alt="Rudder logo"
                  src={RudderLogo}
                />

                <Typography className="mb-1" variant="h5" fontWeight={600}>
                  Welcome Back
                </Typography>

                <form
                  className="w-100 px-3 mb-5"
                  onSubmit={handleSubmit(onSubmit)}
                  id="loginForm"
                  data-testid="loginForm"
                >
                  <TextFieldInput
                    control={control}
                    name="email"
                    rules={{
                      required: validationMessage.emailRequired,
                      pattern: {
                        value: regex.email,
                        message: validationMessage.enterValidEmail,
                      },
                    }}
                    data-testid="email-input"
                    placeholder="Enter Your Email"
                    autoComplete="email"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton tabIndex={-1} type="button">
                            <MailIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextFieldInput
                    control={control}
                    name="password"
                    placeholder="Password"
                    type={showPassword ? "text" : "password"}
                    rules={{
                      required: validationMessage.passwordRequired,
                    }}
                    data-testid="password-input"
                    autoComplete="current-password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={togglePasswordVisibility}
                            tabIndex={-1}
                            type="button"
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <ButtonComponent
                    form="loginForm"
                    type="submit"
                    btnName="Login"
                    fullWidth
                    sx={{ my: 1, borderRadius: 0.5 }}
                    loading={loginLoading}
                  />
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default LoginPage;
